/**
 * AttachmentsList — rich DMS attachment component with search-and-link and
 * drag-and-drop file upload (uploaded files land in DMS, then auto-attach).
 *
 * Use <AttachmentsStrip /> instead for compact, inline contexts.
 */

import { useCallback, useMemo, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { CheckCircle2, ExternalLink, FileText, Loader2, Plus, UploadCloud, X } from 'lucide-react';
import { useDocumentDetail } from '@/features/documents/hooks/useDocuments';
import {
  checkDuplicate,
  computeFileChecksum,
  confirmUpload,
  getUploadUrl,
  uploadToSignedUrl,
} from '@/features/documents/api';
import type { Document } from '@/features/documents/types';
import { DmsDocumentPicker } from './DmsDocumentPicker';
import { useAttachments, useCreateAttachment, useDeleteAttachment } from '../hooks/useAttachments';
import type { AttachmentEntityType } from '../types';

interface AttachmentsListProps {
  entityType: AttachmentEntityType;
  entityId: string;
  emptyText?: string;
  /** Hide the picker, uploader, and per-row delete. Used for read-only viewers. */
  readOnly?: boolean;
  /** Hide the drag-and-drop / upload-file UI while still allowing linking. */
  disableUpload?: boolean;
}

interface UploadItem {
  id: string;
  fileName: string;
  status: 'hashing' | 'uploading' | 'confirming' | 'attaching' | 'done' | 'error';
  error?: string;
}

function formatFileInfo(doc: Document): string {
  const parts: string[] = [];
  const ver = doc.currentVersion;
  if (ver?.originalName) {
    const ext = ver.originalName.split('.').pop()?.toUpperCase();
    if (ext) parts.push(ext);
  }
  if (ver?.pageCount) parts.push(`${ver.pageCount}p`);
  if (doc.documentDate) {
    parts.push(new Date(doc.documentDate).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' }));
  }
  return parts.join(' · ');
}

/** Shows a single attachment row, fetching the document name from DMS. */
function AttachmentRow({
  documentId,
  onDelete,
}: {
  documentId: string;
  onDelete?: () => void;
}) {
  const { data: detail } = useDocumentDetail(documentId);
  const name = detail?.name ?? documentId;
  const dmsUrl = `/documents?doc=${documentId}`;

  return (
    <div className="flex items-center gap-2 group rounded-md px-2 py-1.5 hover:bg-muted/50 transition-colors">
      <FileText className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
      <a
        href={dmsUrl}
        className="text-sm flex-1 truncate hover:underline text-foreground"
        onClick={(e) => e.stopPropagation()}
      >
        {name}
      </a>
      <a
        href={dmsUrl}
        className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-foreground shrink-0"
        title="Open in DMS"
        onClick={(e) => e.stopPropagation()}
      >
        <ExternalLink className="h-3.5 w-3.5" />
      </a>
      {onDelete && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive shrink-0"
          aria-label="Remove attachment"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

export function AttachmentsList({
  entityType,
  entityId,
  emptyText = 'No documents attached.',
  readOnly = false,
  disableUpload = false,
}: AttachmentsListProps) {
  const { data: attachments = [], isLoading } = useAttachments(entityType, entityId);
  const createAttachment = useCreateAttachment();
  const deleteAttachment = useDeleteAttachment();

  const [uploads, setUploads] = useState<UploadItem[]>([]);

  const excludeIds = useMemo(
    () => new Set(attachments.map((a) => a.documentId)),
    [attachments],
  );

  const handleSelect = (doc: Document) => {
    createAttachment.mutate({
      entity_type: entityType,
      entity_id: entityId,
      document_id: doc.id,
    });
  };

  // --- Drag-and-drop upload ---

  const updateUpload = useCallback((id: string, updates: Partial<UploadItem>) => {
    setUploads((prev) => prev.map((u) => (u.id === id ? { ...u, ...updates } : u)));
  }, []);

  const removeUpload = useCallback((id: string) => {
    setUploads((prev) => prev.filter((u) => u.id !== id));
  }, []);

  const uploadQueueRef = useRef<File[]>([]);
  const processingRef = useRef(false);

  const uploadAndAttach = useCallback(
    async (file: File) => {
      const uploadId = crypto.randomUUID();
      setUploads((prev) => [...prev, { id: uploadId, fileName: file.name, status: 'hashing' }]);

      try {
        const checksum = await computeFileChecksum(file);

        // If an identical file already exists in DMS, attach that one instead of re-uploading.
        const { duplicates } = await checkDuplicate(checksum);
        const existing = duplicates.find((d) => d.documentId);
        if (existing) {
          updateUpload(uploadId, { status: 'attaching' });
          await createAttachment.mutateAsync({
            entity_type: entityType,
            entity_id: entityId,
            document_id: existing.documentId,
          });
          updateUpload(uploadId, { status: 'done' });
          setTimeout(() => removeUpload(uploadId), 2000);
          return;
        }

        updateUpload(uploadId, { status: 'uploading' });
        const mimeType = file.type || 'application/octet-stream';
        const { documentId, uploadUrl } = await getUploadUrl(file.name, mimeType, {
          uploadSource: 'ui',
        });
        await uploadToSignedUrl(uploadUrl, file);

        updateUpload(uploadId, { status: 'confirming' });
        await confirmUpload(documentId, file.size, checksum);

        updateUpload(uploadId, { status: 'attaching' });
        await createAttachment.mutateAsync({
          entity_type: entityType,
          entity_id: entityId,
          document_id: documentId,
        });

        updateUpload(uploadId, { status: 'done' });
        setTimeout(() => removeUpload(uploadId), 2000);
      } catch (error) {
        updateUpload(uploadId, {
          status: 'error',
          error: error instanceof Error ? error.message : 'Upload failed',
        });
      }
    },
    [createAttachment, entityId, entityType, removeUpload, updateUpload],
  );

  const processQueue = useCallback(async () => {
    if (processingRef.current) return;
    processingRef.current = true;
    while (uploadQueueRef.current.length > 0) {
      const file = uploadQueueRef.current.shift()!;
      await uploadAndAttach(file);
    }
    processingRef.current = false;
  }, [uploadAndAttach]);

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length === 0) return;
      uploadQueueRef.current.push(...acceptedFiles);
      processQueue();
    },
    [processQueue],
  );

  const uploadEnabled = !readOnly && !disableUpload;

  const { getRootProps, getInputProps, isDragActive, open: openFilePicker } = useDropzone({
    onDrop,
    multiple: true,
    noClick: true,
    noKeyboard: true,
    disabled: !uploadEnabled,
  });

  const activeUploads = uploads.filter((u) => u.status !== 'done');

  if (isLoading) {
    return (
      <div className="space-y-1.5">
        {[1, 2].map((i) => (
          <div key={i} className="h-8 rounded bg-muted animate-pulse" />
        ))}
      </div>
    );
  }

  const rootProps = uploadEnabled ? getRootProps() : {};

  return (
    <div
      {...rootProps}
      className={`relative space-y-1 rounded-md transition-colors ${
        isDragActive ? 'ring-2 ring-primary ring-offset-2 bg-primary/5' : ''
      }`}
    >
      {uploadEnabled && <input {...getInputProps()} />}

      {isDragActive && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-md bg-background/80 z-10">
          <div className="flex items-center gap-2 text-sm font-medium text-primary">
            <UploadCloud className="h-4 w-4" />
            Drop files to upload and attach
          </div>
        </div>
      )}

      {attachments.length === 0 && activeUploads.length === 0 ? (
        <p className="text-xs text-muted-foreground py-1">{emptyText}</p>
      ) : (
        attachments.map((attachment) => (
          <AttachmentRow
            key={attachment.id}
            documentId={attachment.documentId}
            onDelete={
              readOnly
                ? undefined
                : () => deleteAttachment.mutate({ id: attachment.id, entityType, entityId })
            }
          />
        ))
      )}

      {activeUploads.length > 0 && (
        <div className="space-y-1 pt-1">
          {activeUploads.map((upload) => (
            <div
              key={upload.id}
              className="flex items-center gap-2 rounded-md px-2 py-1.5 bg-muted/30 text-xs"
            >
              {upload.status === 'error' ? (
                <X className="h-3.5 w-3.5 text-destructive shrink-0" />
              ) : upload.status === 'done' ? (
                <CheckCircle2 className="h-3.5 w-3.5 text-success shrink-0" />
              ) : (
                <Loader2 className="h-3.5 w-3.5 animate-spin text-primary shrink-0" />
              )}
              <span className="flex-1 truncate">{upload.fileName}</span>
              <span className="text-muted-foreground capitalize">
                {upload.error ?? upload.status}
              </span>
            </div>
          ))}
        </div>
      )}

      {!readOnly && (
        <div className="flex items-center gap-3 pt-0.5">
          <DmsDocumentPicker
            size="md"
            excludeIds={excludeIds}
            onSelect={handleSelect}
            formatSecondary={(doc) =>
              formatFileInfo(doc) || doc.currentVersion?.originalName || doc.id
            }
            trigger={
              <button
                type="button"
                className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors py-1 px-2"
              >
                <Plus className="h-3 w-3" />
                Link DMS Document
              </button>
            }
          />

          {uploadEnabled && (
            <button
              type="button"
              onClick={openFilePicker}
              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors py-1 px-2"
            >
              <UploadCloud className="h-3 w-3" />
              Upload file (or drop here)
            </button>
          )}
        </div>
      )}
    </div>
  );
}
